import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { UserActions } from '@next/core-lib/user';
import { ofType, createEffect, Actions } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';

import { of, EMPTY, forkJoin, from } from 'rxjs';
import {
  switchMap,
  concatMap,
  finalize,
  startWith,
  map,
  withLatestFrom,
  tap,
  take,
  catchError,
} from 'rxjs/operators';

import {
  AppActionTypes,
  GetCrops,
  GetTemplates,
  SetNutrientProperties,
  SetCrops,
  SetTemplates,
  CreateTemplate,
  FetchTemplatesAndRedirect,
  RedirectToTemplate,
  RemoveTemplate,
  SetAvailableCropYears,
  SetDefaultCropYear,
  SetCropYear,
  GetSalesOrderPeriods,
  SetSalesOrderPeriods,
  CopyTemplatesByYear,
  ResetCropYear,
  SetCropYearInStore,
  DeleteNutrientProperties,
  GetNutrientProperties,
  SetSoilTypes,
  SetUnitsOfMeasure,
  UpsertNutrientProperties,
  SetNutrientDesignations,
  SetNutrientSources,
  OpenEditNutrientPropertyDialog,
  SetVarietyProperties,
  UpsertVarietyProperties,
  GetVarietyProperties,
  DeleteVarietyProperties,
  OpenEditVarietyPropertyDialog,
  SetCropVarieties,
  SetProductionPurposeCategories,
  SetNitrogenModelProperties,
  UpsertNitrogenModelProperties,
  GetNitrogenModelProperties,
  DeleteNitrogenModelProperties,
  OpenEditNitrogenModelPropertyDialog,
  SetProductionPurposeCategoryDetailed,
  GetProductionPurposeCategoriesDetailed,
  UpsertProductionPurposeCategory,
  DeleteProductionPurposeCategory,
  OpenEditProductionPurposeCategoryDialog,
} from './actions';
import {
  selectCrops,
  selectAvailableCropYears,
  getDefaultCropYear,
  getCropYear,
  getTemplatePagination,
  selectQueryCropYear,
  getTemplateFilter,
  selectUnitsOfMeasure,
  selectNutrientDesignations,
  selectNutrientSources,
  selectNutrientSoilTypes,
  selectCropVarieties,
  selectProductionPurposeCategories,
  selectVarietyProperties,
} from './selectors';
import { CopyTemplatesByYearDialogComponent } from '../shared/components/copy-templates-by-year-dialog/copy-templates-by-year-dialog.component';
// eslint-disable-next-line max-len
import { CreateNitrogenModelPropertyDialogComponent } from '../shared/components/create-nitrogen-model-property-dialog/create-nitrogen-model-property-dialog.component';
import { CreateNutrientPropertyDialogComponent } from '../shared/components/create-nutrient-property-dialog/create-nutrient-property-dialog.component';
// eslint-disable-next-line max-len
import { CreateProductionPurposeCategoryDialogComponent } from '../shared/components/create-production-purpose-category-dialog/create-production-purpose-category-dialog.component';
import { CreateTemplateDialogComponent } from '../shared/components/create-template-dialog/create-template-dialog.component';
import { CreateVarietyPropertyDialogComponent } from '../shared/components/create-variety-property-dialog/create-variety-property-dialog.component';
import {
  AppState,
  Crop,
  CopyTemplatesByYearResponse,
  PaginationState,
  TemplateFilter,
  UnitOfMeasure,
  NutrientDesignation,
  NutrientSource,
  CropVariety,
  ProductionPurposeCategory,
  UpsertVarietyPropertiesRequest,
  VarietyProperties,
} from '../shared/models';
import { ApiService } from '../shared/services/api.service';

import { CropApiService } from '../shared/services/crop.service';
import { SoilType } from '../soil-type/soil-type.model';
import { selectSoilTypes } from '../soil-type/store/soil-type.selectors';

function handleVarietyPropertyDialogResult(
  status: string,
  result: UpsertVarietyPropertiesRequest | UpsertVarietyPropertiesRequest[],
) {
  if (status === DIALOG_RETURN_TYPES.cancel) {
    return EMPTY;
  }
  if (Array.isArray(result)) {
    return from(result).pipe(map((r) => new UpsertVarietyProperties(r)));
  }
  return of(new UpsertVarietyProperties(result));
}

@Injectable()
export class AppEffects {
  protected store = inject<Store<AppState>>(Store);
  protected readonly router = inject(Router);
  protected actions$ = inject(Actions);
  protected api = inject(ApiService);
  protected cropApi = inject(CropApiService);
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly route = inject(ActivatedRoute);

  userInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitialized),
      switchMap((_) => [
        new SetDefaultCropYear(new Date().getFullYear()),
        new SetCropYear(new Date().getFullYear()),
        new GetCrops(),
      ]),
    ),
  );

  getVarietyProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetVarietyProperties),
      switchMap(() =>
        this.api.getAllVarietyProperties().pipe(
          map((varietyProperties) => new SetVarietyProperties(varietyProperties)),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  upsertNutrientProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.UpsertNutrientProperties),
      switchMap(({ request }: UpsertNutrientProperties) =>
        this.api.upsertNutrientProperties(request).pipe(
          map(() => new GetNutrientProperties()),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  deleteNutrientProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.DeleteNutrientProperties),
      switchMap(({ id }: DeleteNutrientProperties) =>
        this.api.deleteNutrientProperties(id).pipe(
          map(() => new GetNutrientProperties()),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  openEditNutrientPropertyDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenEditNutrientPropertyDialog),
      withLatestFrom(
        this.store.pipe(select(selectNutrientSoilTypes)),
        this.store.pipe(select(selectUnitsOfMeasure)),
        this.store.pipe(select(selectNutrientDesignations)),
        this.store.pipe(select(selectNutrientSources)),
      ),
      switchMap(
        ([action, soilTypes, unitsOfMeasure, nutrientDesignations, nutrientSources]: [
          OpenEditNutrientPropertyDialog,
          SoilType[],
          UnitOfMeasure[],
          NutrientDesignation[],
          NutrientSource[],
        ]) => {
          const { nutrientProperty } = action.payload;
          return this.dialog
            .form(CreateNutrientPropertyDialogComponent, {
              title: this.locale.translate('nutrientProperties.upsertNutrientProperty'),
              cancelButtonText: this.locale.translate('cancel'),
              confirmButtonText: this.locale.translate('save'),
              soilTypes,
              unitsOfMeasure,
              nutrientDesignations,
              nutrientSources,
              nutrientProperty,
              fullscreen: false,
              disableClose: true,
            })
            .pipe(
              switchMap(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.cancel) {
                  return EMPTY;
                }
                result.id = nutrientProperty.id;
                return of(new UpsertNutrientProperties(result));
              }),
            );
        },
      ),
    ),
  );

  getNutrientProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetNutrientProperties),
      switchMap(() =>
        this.api.getAllNutrientProperties().pipe(
          map((nutrientProperties) => new SetNutrientProperties(nutrientProperties)),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getSoilTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetSoilTypes),
      switchMap(() =>
        this.api.getAllSoilTypes().pipe(
          map((soilTypes) => new SetSoilTypes(soilTypes)),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getUnitsOfMeasure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetUnitsOfMeasure),
      switchMap(() =>
        this.api.getAllUnitsOfMeasure().pipe(
          map((unitsOfMeasure) => new SetUnitsOfMeasure(unitsOfMeasure)),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getNutrientDesignations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetNutrientDesignations),
      switchMap(() =>
        this.api.getNutrientDesignations().pipe(
          map((nutrientDesignations) => new SetNutrientDesignations(nutrientDesignations)),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getNutrientSources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetNutrientSources),
      switchMap(() =>
        this.api.getAllNutrientSources().pipe(
          map((nutrientSources) => new SetNutrientSources(nutrientSources)),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  setDefaultCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.SetDefaultCropYear),
      withLatestFrom(this.store.pipe(select(getDefaultCropYear))),
      map(
        ([_, defaultCropYear]) =>
          new SetAvailableCropYears(
            isNaN(defaultCropYear) || typeof defaultCropYear !== 'number'
              ? []
              : [defaultCropYear - 1, defaultCropYear, defaultCropYear + 1],
          ),
      ),
    ),
  );

  resetCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ResetCropYear),
      withLatestFrom(
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(selectAvailableCropYears)),
      ),
      switchMap(([{ cropYear }, currentCropYear, availableCropYears]) => {
        const currentYear = new Date().getFullYear();
        let newCropYear: number = cropYear;

        if (
          isNaN(newCropYear) ||
          typeof newCropYear !== 'number' ||
          availableCropYears.indexOf(newCropYear) === -1
        ) {
          newCropYear = currentYear;
        }

        if (newCropYear !== currentCropYear) {
          return of(new SetCropYear(newCropYear));
        }

        return EMPTY;
      }),
    ),
  );

  setCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.SetCropYear),
      tap(({ cropYear }) => {
        const isCropYearDefault = cropYear === new Date().getFullYear();
        const hasCropYearParam = this.route.snapshot.queryParamMap.has('cropYear');
        const currentCropYearParam = this.route.snapshot.queryParamMap.get('cropYear');
        const shouldUpdate =
          (isCropYearDefault && hasCropYearParam) ||
          (!isCropYearDefault && !hasCropYearParam) ||
          (!isCropYearDefault && currentCropYearParam !== cropYear);
        if (shouldUpdate) {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: isCropYearDefault ? {} : { cropYear },
            queryParamsHandling: '',
            replaceUrl: true,
          });
        }
      }),
      switchMap(() => [new GetSalesOrderPeriods()]),
    ),
  );

  getCrops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetCrops),
      switchMap((_: GetCrops) =>
        this.cropApi.getCrops().pipe(
          map((crops: Crop[]) => new SetCrops(crops)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetTemplates),
      withLatestFrom(
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(getTemplatePagination)),
        this.store.pipe(select(getTemplateFilter)),
      ),
      switchMap(
        ([action, cropYear, paginationState, templateFilter]: [
          { pageNumber: number; pageSize: number },
          number,
          PaginationState,
          TemplateFilter,
        ]) =>
          this.api
            .getTemplates(
              cropYear,
              action.pageNumber ? action.pageNumber : paginationState.pageNumber,
              action.pageSize ? action.pageSize : paginationState.pageSize,
              templateFilter,
            )
            .pipe(
              map(({ templates, pagination }) => new SetTemplates(templates, pagination)),
              finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
              startWith(CoreActions.setLoading({ loading: true })),
              catchError((_) =>
                of(new SetTemplates([], { totalNumberOfItems: 0, pageNumber: 1, pageSize: 10 })),
              ),
            ),
      ),
    ),
  );

  getSalesOrderPeriods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetSalesOrderPeriods),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([_, cropYear]) =>
        this.api.getSalesOrderPeriods(cropYear).pipe(
          map((salesOrderPeriods) => new SetSalesOrderPeriods(salesOrderPeriods)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  openCreateTemplateDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenCreateTemplateDialog),
      withLatestFrom(
        this.store.pipe(select(selectAvailableCropYears)),
        this.store.pipe(select(selectCrops)),
        this.store.pipe(select(selectSoilTypes)),
      ),
      switchMap(
        ([_, cropYears, crops, soilTypes]: [
          AppActionTypes.OpenCreateTemplateDialog,
          number[],
          Crop[],
          SoilType[],
        ]) => {
          const soilTypeIndependent: SoilType = {
            id: 0,
            name: this.locale.translate('soilTypeIndependent'),
          };

          return this.dialog
            .form(CreateTemplateDialogComponent, {
              title: this.locale.translate('createTemplate'),
              cancelButtonText: this.locale.translate('cancel'),
              confirmButtonText: this.locale.translate('save'),
              crops,
              soilTypes: [soilTypeIndependent, ...soilTypes],
              cropYears,
              fullscreen: false,
              disableClose: true,
            })
            .pipe(
              switchMap(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.cancel) {
                  return EMPTY;
                }

                result.soilTypeId =
                  result.soilTypeId === soilTypeIndependent.id ? null : result.soilTypeId;
                return of(new CreateTemplate(result));
              }),
            );
        },
      ),
    ),
  );

  createTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.CreateTemplate),
      switchMap(({ template }: CreateTemplate) =>
        this.api.upsertTemplate(template).pipe(
          map((response) => new FetchTemplatesAndRedirect(response.id)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  fetchTemplatesAndRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.FetchTemplatesAndRedirect),
      switchMap(({ id }: FetchTemplatesAndRedirect) =>
        this.api.getTemplate(id).pipe(
          switchMap((template) =>
            this.api
              .getSalesOrderPeriods(template.cropYear)
              .pipe(
                switchMap((salesOrderPeriods) =>
                  of(
                    new SetSalesOrderPeriods(salesOrderPeriods),
                    new SetCropYearInStore(template.cropYear),
                    new RedirectToTemplate(id, template.cropYear),
                  ),
                ),
              ),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  redirectToTemplate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActionTypes.RedirectToTemplate),
        tap(({ id, cropYear }: RedirectToTemplate) => {
          if (cropYear === new Date().getFullYear()) {
            this.router.navigate([`template/${id}`]);
          }

          this.router.navigate([`template/${id}`], {
            queryParams: { cropYear },
            queryParamsHandling: '',
            replaceUrl: true,
            relativeTo: this.route,
          });
        }),
      ),
    { dispatch: false },
  );

  removeTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.RemoveTemplate),
      switchMap(({ id }: RemoveTemplate) =>
        this.dialog
          .confirm({
            title: this.locale.translate('deleteTemplateConfirmTitle'),
            text: this.locale.translate('deleteTemplateConfirmText'),
          })
          .pipe(
            switchMap(({ status }) =>
              status === DIALOG_RETURN_TYPES.confirm
                ? this.api.removeTemplate(id).pipe(
                    switchMap(() => [new GetTemplates()]),
                    startWith(CoreActions.setLoading({ loading: true })),
                    finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                  )
                : EMPTY,
            ),
          ),
      ),
    ),
  );

  openCopyTemplatesByYearDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenCopyTemplatesByYearDialog),
      withLatestFrom(this.store.pipe(select(selectAvailableCropYears))),
      switchMap(([_, cropYears]: [never, number[]]) =>
        this.dialog
          .form(CopyTemplatesByYearDialogComponent, {
            title: this.locale.translate('copyTemplatesByYear'),
            cancelButtonText: this.locale.translate('cancel'),
            confirmButtonText: this.locale.translate('save'),
            cropYears,
            disableClose: true,
            fullscreen: false,
          })
          .pipe(
            switchMap(({ status, result }) =>
              status === DIALOG_RETURN_TYPES.confirm
                ? [new CopyTemplatesByYear({ ...result })]
                : EMPTY,
            ),
          ),
      ),
    ),
  );

  copyTemplatesByYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.CopyTemplatesByYear),
      switchMap(({ request }: CopyTemplatesByYear) =>
        this.api.copyTemplatesByYear(request).pipe(
          switchMap((data: CopyTemplatesByYearResponse) => [
            NotificationActions.successSnackbar({
              message: data.numberOfTemplatesCopied
                .toString()
                .concat(this.locale.translate('copyTemplatesByYearQueued')),
            }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  routerNavigated$ = createEffect(() =>
    forkJoin([
      this.actions$.pipe(ofType(ROUTER_NAVIGATED), take(1)),
      this.actions$.pipe(ofType(UserActions.customerInitialized), take(1)),
    ]).pipe(
      withLatestFrom(this.store.pipe(select(selectQueryCropYear))),
      map(([[_, __], cropYear]) => new ResetCropYear(cropYear ? parseInt(cropYear, 10) : null)),
    ),
  );

  upsertVarietyProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.UpsertVarietyProperties),
      concatMap(({ request }: UpsertVarietyProperties) =>
        this.api.upsertVarietyProperties(request).pipe(
          map(() => new GetVarietyProperties()),

          catchError((error: HttpErrorResponse) => {
            if (
              error.status === 400 &&
              error.error?.validationMessageCodes?.includes('VarietyPropertiesMustBeUnique')
            ) {
              return of(
                NotificationActions.errorSnackbar({
                  message: this.locale.translate('varietyProperties.uniqueVarietyProperty'),
                }),
              );
            }
            return of(NotificationActions.errorSnackbar({ message: error.message }));
          }),

          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  deleteVarietyProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.DeleteVarietyProperties),
      switchMap(({ id }: DeleteVarietyProperties) =>
        this.api.deleteVarietyProperties(id).pipe(
          map(() => new GetVarietyProperties()),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  openCreateVarietyPropertyDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenCreateVarietyPropertyDialog),
      withLatestFrom(
        this.store.pipe(select(selectCropVarieties)),
        this.store.pipe(select(selectProductionPurposeCategories)),
        this.store.pipe(select(selectVarietyProperties)),
      ),
      switchMap(
        ([_, cropVarieties, productionPurposeCategories, existingVarietyProperties]: [
          AppActionTypes.OpenCreateVarietyPropertyDialog,
          CropVariety[],
          ProductionPurposeCategory[],
          VarietyProperties[],
        ]) => {
          return this.dialog
            .form(CreateVarietyPropertyDialogComponent, {
              title: this.locale.translate('varietyProperties.upsertVarietyProperty'),
              cancelButtonText: this.locale.translate('cancel'),
              confirmButtonText: this.locale.translate('save'),
              cropVarieties,
              productionPurposeCategories,
              existingVarietyProperties,
              fullscreen: false,
              disableClose: true,
            })
            .pipe(
              switchMap(({ status, result }) => handleVarietyPropertyDialogResult(status, result)),
            );
        },
      ),
    ),
  );

  openEditVarietyPropertyDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenEditVarietyPropertyDialog),
      withLatestFrom(
        this.store.pipe(select(selectCropVarieties)),
        this.store.pipe(select(selectProductionPurposeCategories)),
      ),
      switchMap(
        ([action, cropVarieties, productionPurposeCategories]: [
          OpenEditVarietyPropertyDialog,
          CropVariety[],
          ProductionPurposeCategory[],
        ]) => {
          const varietyProperty = action.payload.varietyProperty;
          const providedExisting = action.payload.existingVarietyProperties;

          const existing$ = providedExisting
            ? of(providedExisting)
            : this.api
                .getAllVarietyProperties()
                .pipe(map((all) => all.filter((v) => v.ediCode === varietyProperty.ediCode)));

          return existing$.pipe(
            switchMap((existingForEdi) =>
              this.dialog
                .form(CreateVarietyPropertyDialogComponent, {
                  title: this.locale.translate('varietyProperties.upsertVarietyProperty'),
                  cancelButtonText: this.locale.translate('cancel'),
                  confirmButtonText: this.locale.translate('save'),
                  cropVarieties,
                  productionPurposeCategories,
                  varietyProperty,
                  existingVarietyProperties: existingForEdi,
                  fullscreen: false,
                  disableClose: true,
                })
                .pipe(
                  switchMap(({ status, result }) => {
                    if (status === DIALOG_RETURN_TYPES.cancel) {
                      return EMPTY;
                    }
                    if (Array.isArray(result)) {
                      return from(result as UpsertVarietyPropertiesRequest[]).pipe(
                        map((r) => new UpsertVarietyProperties(r)),
                      );
                    }
                    return of(new UpsertVarietyProperties(result));
                  }),
                ),
            ),
          );
        },
      ),
    ),
  );

  getCropVarieties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetCropVarieties),
      switchMap(() =>
        this.api.getAllCropVarieties().pipe(
          map((cropVarieties) => new SetCropVarieties(cropVarieties)),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getProductionPurposeCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetProductionPurposeCategories),
      switchMap(() =>
        this.api.getAllProductionPurposeCategories().pipe(
          map(
            (productionPurposeCategories) =>
              new SetProductionPurposeCategories(productionPurposeCategories),
          ),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getNitrogenModelProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetNitrogenModelProperties),
      switchMap(() =>
        this.api.getAllNitrogenModelProperties().pipe(
          map((nitrogenModelProperties) => new SetNitrogenModelProperties(nitrogenModelProperties)),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  upsertNitrogenModelProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.UpsertNitrogenModelProperties),
      switchMap(({ request }: UpsertNitrogenModelProperties) =>
        this.api.upsertNitrogenModelProperties(request).pipe(
          map(() => new GetNitrogenModelProperties()),

          catchError((error: HttpErrorResponse) => {
            if (
              error.status === 400 &&
              error.error?.validationMessageCodes?.includes('NitrogenModelPropertiesMustBeUnique')
            ) {
              return of(
                NotificationActions.errorSnackbar({
                  message: this.locale.translate(
                    'nitrogenModelProperties.uniqueNitrogenModelProperty',
                  ),
                }),
              );
            }
            return of(NotificationActions.errorSnackbar({ message: error.message }));
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  deleteNitrogenModelProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.DeleteNitrogenModelProperties),
      switchMap(({ id }: DeleteNitrogenModelProperties) =>
        this.api.deleteNitrogenModelProperties(id).pipe(
          map(() => new GetNitrogenModelProperties()),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  openCreateNitrogenModelPropertyDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenCreateNitrogenModelPropertyDialog),
      withLatestFrom(
        this.store.pipe(select(selectSoilTypes)),
        this.store.pipe(select(selectUnitsOfMeasure)),
        this.store.pipe(select(selectNutrientDesignations)),
      ),
      switchMap(
        ([_, soilTypes, unitsOfMeasure, nutrientDesignations]: [
          AppActionTypes.OpenCreateNitrogenModelPropertyDialog,
          SoilType[],
          UnitOfMeasure[],
          NutrientDesignation[],
        ]) =>
          this.dialog
            .form(CreateNitrogenModelPropertyDialogComponent, {
              title: this.locale.translate('nitrogenModelProperties.upsertNitrogenModelProperty'),
              cancelButtonText: this.locale.translate('cancel'),
              confirmButtonText: this.locale.translate('save'),
              soilTypes,
              unitsOfMeasure,
              nutrientDesignations,
              fullscreen: false,
              disableClose: true,
            })
            .pipe(
              switchMap(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.cancel) {
                  return EMPTY;
                }
                return of(new UpsertNitrogenModelProperties(result));
              }),
            ),
      ),
    ),
  );

  openEditNitrogenModelPropertyDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenEditNitrogenModelPropertyDialog),
      withLatestFrom(
        this.store.pipe(select(selectSoilTypes)),
        this.store.pipe(select(selectUnitsOfMeasure)),
        this.store.pipe(select(selectNutrientDesignations)),
      ),
      switchMap(
        ([action, soilTypes, unitsOfMeasure, nutrientDesignations]: [
          OpenEditNitrogenModelPropertyDialog,
          SoilType[],
          UnitOfMeasure[],
          NutrientDesignation[],
        ]) => {
          const { nitrogenModelProperties } = action.payload;

          return this.dialog
            .form(CreateNitrogenModelPropertyDialogComponent, {
              title: this.locale.translate('nitrogenModelProperties.upsertNitrogenModelProperty'),
              cancelButtonText: this.locale.translate('cancel'),
              confirmButtonText: this.locale.translate('save'),
              soilTypes,
              unitsOfMeasure,
              nutrientDesignations,
              nitrogenModelProperties,
              fullscreen: false,
              disableClose: true,
            })
            .pipe(
              switchMap(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.cancel) {
                  return EMPTY;
                }
                result.id = nitrogenModelProperties.id;
                return of(new UpsertNitrogenModelProperties(result));
              }),
            );
        },
      ),
    ),
  );

  getProductionPurposeCategoryDetailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetProductionPurposeCategoriesDetailed),
      switchMap(() =>
        this.api.getProductionPurposeCategoriesDetailed().pipe(
          map(
            (productionPurposeCategories) =>
              new SetProductionPurposeCategoryDetailed(productionPurposeCategories),
          ),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  upsertProductionPurposeCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.UpsertProductionPurposeCategoriesDetailed),
      switchMap(({ request }: UpsertProductionPurposeCategory) =>
        this.api.upsertProductionPurposeCategory(request).pipe(
          map(() => new GetProductionPurposeCategoriesDetailed()),

          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),

          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  deleteProductionPurposeCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.DeleteProductionPurposeCategory),
      switchMap(({ id }: DeleteProductionPurposeCategory) =>
        this.api.deleteProductionPurposeCategories(id).pipe(
          map(() => new GetProductionPurposeCategoriesDetailed()),
          catchError((error: HttpErrorResponse) => {
            if (
              error.status === 400 &&
              error.error?.validationMessageCodes?.includes(
                'ProductionPurposeCategoriesMustNotBeLinkedToOtherEntities',
              )
            ) {
              return of(
                NotificationActions.errorSnackbar({
                  message: this.locale.translate(
                    'productionPurposeCategory.deleteMustNotBeLinkedToOtherEntitiesError',
                  ),
                }),
              );
            }
            return of(NotificationActions.errorSnackbar({ message: error.message }));
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  openCreateProductionPurposeCategoryDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenCreateProductionPurposeCategoriesDialog),
      switchMap(() =>
        this.dialog
          .form(CreateProductionPurposeCategoryDialogComponent, {
            title: this.locale.translate(
              'productionPurposeCategory.upsertProductionPurposeCategory',
            ),
            cancelButtonText: this.locale.translate('cancel'),
            confirmButtonText: this.locale.translate('save'),
            productionPurposeCategory: null,
            fullscreen: false,
            disableClose: true,
          })
          .pipe(
            switchMap(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.cancel) {
                return EMPTY;
              }
              return of(new UpsertProductionPurposeCategory(result));
            }),
          ),
      ),
    ),
  );

  openEditProductionPurposeCategoryPropertyDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenEditProductionPurposeCategoryDialog),
      switchMap((action: OpenEditProductionPurposeCategoryDialog) => {
        const { productionPurposeCategory } = action.payload;

        return this.dialog
          .form(CreateProductionPurposeCategoryDialogComponent, {
            title: this.locale.translate(
              'productionPurposeCategory.upsertProductionPurposeCategory',
            ),
            cancelButtonText: this.locale.translate('cancel'),
            confirmButtonText: this.locale.translate('save'),
            productionPurposeCategory,
            fullscreen: false,
            disableClose: true,
          })
          .pipe(
            switchMap(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.cancel) {
                return EMPTY;
              }
              result.id = productionPurposeCategory.id;
              return of(new UpsertProductionPurposeCategory(result));
            }),
          );
      }),
    ),
  );
}
